import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText, Layers, Download, Send } from "lucide-react";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";
import { PDF_FOOTER_TEXT, PDF_HEADER_TEXT, DISCLAIMER_VERSION } from "../lib/disclaimer";

export default function TreatmentPlanDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("phases");

  useEffect(() => {
    api.get(`/treatment-plan/${id}`)
      .then((r) => setDoc(r.data))
      .catch((e) => toast.error(errMsg(e, "Failed to load")))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Layout><div className="flex items-center gap-2 text-[var(--pa-text-muted)] text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div></Layout>;
  if (!doc) return <Layout><div className="text-sm text-[var(--pa-text-muted)]">Not found.</div></Layout>;

  const p = doc.plan || {};
  const phases = p.phases || [];
  const options = p.options || [];

  const dl = (filename, content) => {
    // Prepend the disclaimer header so the downloaded artifact carries the
    // legal notice even if it leaves the platform.
    const stamped =
      `> ${PDF_HEADER_TEXT}\n` +
      `> ${PDF_FOOTER_TEXT}\n` +
      `> Disclaimer ${DISCLAIMER_VERSION}\n\n` +
      "---\n\n" +
      content;
    const blob = new Blob([stamped], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="kicker">Phase 2 · Treatment Planning</div>
      <h1 data-testid="tx-plan-detail-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <FileText size={22} className="text-[var(--pa-gold)]" /> Treatment Plan — {doc.patient_name}
      </h1>
      <div className="mt-1 text-xs text-[var(--pa-text-dim)]">{new Date(doc.created_at).toLocaleString()}</div>

      <AiOutputDisclaimer className="mt-4" />

      <div className="mt-3 flex flex-wrap gap-2">
        <Link to={`/case-presentation/${doc.id}`} data-testid="tx-to-presentation"
          className="inline-flex items-center gap-1.5 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-4 py-2 rounded text-sm hover:bg-[var(--pa-gold-hover)] transition">
          <Send size={13} /> Case Presentation &amp; Email Patient
        </Link>
        {doc.claim_status && (
          <span className="inline-flex items-center px-3 py-1.5 rounded text-xs border border-[var(--pa-border)] bg-[var(--pa-bg-2)] text-[var(--pa-text-muted)]">
            Claim: <span className="ml-1 text-[var(--pa-gold)] font-mono">{doc.claim_status}</span>
          </span>
        )}
        {doc.case_acceptance && (
          <span className="inline-flex items-center px-3 py-1.5 rounded text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
            {doc.case_acceptance}{doc.accepted_option ? ` · Option ${doc.accepted_option}` : ""}
          </span>
        )}
      </div>

      {/* Modules used */}
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="tx-plan-modules">
        <span className="text-[10px] uppercase tracking-wider text-[var(--pa-text-dim)] mr-1">Synthesized from</span>
        {(doc.modules_included || []).map((m) => (
          <span key={m} className="text-[10px] px-2 py-0.5 rounded bg-[var(--pa-gold)]/15 border border-[var(--pa-gold)]/40 text-[var(--pa-gold)]">{m.replace(/_/g, " ")}</span>
        ))}
      </div>

      {p.summary && (
        <p className="mt-5 text-sm text-[var(--pa-text)] leading-relaxed bg-[var(--pa-bg-2)] border-l-2 border-[var(--pa-gold)] p-4 rounded-r" data-testid="tx-plan-summary">
          {p.summary}
        </p>
      )}

      {/* Tabs */}
      <div className="mt-6 flex gap-2 border-b border-[var(--pa-border)]" data-testid="tx-plan-tabs">
        {[
          ["phases", "Phased Plan"],
          ["options", "3 Options"],
          ["patient", "Patient Summary"],
          ["soap", "SOAP Note"],
          ["preauth", "Pre-Auth Letter"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tx-tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${tab === k ? "border-[var(--pa-gold)] text-[var(--pa-gold)]" : "border-transparent text-[var(--pa-text-muted)] hover:text-[var(--pa-text)]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "phases" && (
        <div className="mt-6 space-y-4" data-testid="tx-plan-phases">
          {phases.map((ph, i) => (
            <div key={i} className="border border-[var(--pa-border)] rounded-md bg-[var(--pa-bg-2)] overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--pa-border)]">
                <Layers size={16} className="text-[var(--pa-gold)]" />
                <span className="font-semibold text-[var(--pa-text)]">Phase {ph.phase_number} — {ph.label}</span>
                <span className="ml-auto text-xs text-[var(--pa-text-dim)]">{(ph.procedures || []).length} procedure(s)</span>
              </div>
              {ph.rationale && <p className="px-4 py-2 text-xs text-[var(--pa-text-muted)] italic border-b border-[var(--pa-border)]">{ph.rationale}</p>}
              <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[var(--pa-bg)] text-[10px] uppercase tracking-wider text-[var(--pa-text-dim)]">
                  <tr>
                    <th className="text-left px-3 py-2">Seq</th>
                    <th className="text-left px-3 py-2">CDT</th>
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-left px-3 py-2">Tooth</th>
                    <th className="text-right px-3 py-2">Fee</th>
                    <th className="text-center px-3 py-2">Min</th>
                    <th className="text-center px-3 py-2">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {(ph.procedures || []).map((pr, j) => (
                    <tr key={j} className="border-t border-[var(--pa-border)] align-top">
                      <td className="px-3 py-2 text-[var(--pa-text-dim)]">{pr.sequence}</td>
                      <td className="px-3 py-2 font-mono text-[var(--pa-gold)] font-semibold">{pr.cdt_code}</td>
                      <td className="px-3 py-2 text-[var(--pa-text)]">
                        {pr.description}
                        {pr.justification && <div className="text-[10px] text-[var(--pa-text-muted)] mt-0.5">{pr.justification}</div>}
                      </td>
                      <td className="px-3 py-2 text-[var(--pa-text-muted)]">{(pr.teeth || []).join(", ") || "—"}</td>
                      <td className="px-3 py-2 text-right text-[var(--pa-text)]">${pr.fee_usd ?? "—"}</td>
                      <td className="px-3 py-2 text-center text-[var(--pa-text-dim)]">{pr.appointment_minutes ?? "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex flex-col gap-0.5 items-center">
                          {pr.pre_auth_required && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300">PRE-AUTH</span>}
                          {pr.narrative_required && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 border border-blue-500/40 text-blue-300">NARR</span>}
                          {(pr.contraindication_flags || []).map((f, k) => (
                            <span key={k} className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-red-300">{f.replace(/_/g, " ")}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "options" && (
        <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="tx-plan-options">
          {options.map((o, i) => (
            <div key={i} className={`border rounded-md p-4 bg-[var(--pa-bg-2)] ${i === 0 ? "border-[var(--pa-gold)]" : "border-[var(--pa-border)]"}`}>
              <div className="font-serif text-xl text-[var(--pa-text)]">{o.label}</div>
              <p className="text-xs text-[var(--pa-text-muted)] mt-1">{o.summary}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-[var(--pa-bg)] rounded p-2">
                  <div className="text-[10px] text-[var(--pa-text-dim)] uppercase">Total fee</div>
                  <div className="font-semibold text-[var(--pa-gold)]">${(o.total_fee_usd || 0).toLocaleString()}</div>
                </div>
                <div className="bg-[var(--pa-bg)] rounded p-2">
                  <div className="text-[10px] text-[var(--pa-text-dim)] uppercase">Patient pays</div>
                  <div className="font-semibold text-[var(--pa-text)]">${(o.estimated_patient_usd || 0).toLocaleString()}</div>
                </div>
              </div>
              <div className="text-[10px] text-[var(--pa-text-dim)] mt-2">Est. insurance: ${(o.estimated_insurance_usd || 0).toLocaleString()}</div>
              {o.pros?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase text-emerald-400 mb-1">Pros</div>
                  <ul className="text-xs text-[var(--pa-text-muted)] list-disc ml-4 space-y-0.5">{o.pros.map((x, k) => <li key={k}>{x}</li>)}</ul>
                </div>
              )}
              {o.cons?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase text-amber-400 mb-1">Cons</div>
                  <ul className="text-xs text-[var(--pa-text-muted)] list-disc ml-4 space-y-0.5">{o.cons.map((x, k) => <li key={k}>{x}</li>)}</ul>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1">
                {(o.procedure_cdt_codes || []).map((c, k) => (
                  <span key={k} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[var(--pa-bg)] border border-[var(--pa-border)] text-[var(--pa-text-muted)]">{c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "patient" && (
        <TextBlock title="Patient-friendly summary" text={p.patient_summary_markdown} filename={`patient-summary-${doc.id}.md`} dl={dl} testid="tx-patient-summary" />
      )}
      {tab === "soap" && (
        <TextBlock title="SOAP clinical note" text={p.clinical_notes_soap_markdown} filename={`soap-${doc.id}.md`} dl={dl} testid="tx-soap" />
      )}
      {tab === "preauth" && (
        <TextBlock title="Pre-authorization letter" text={p.preauth_letter_markdown} filename={`preauth-${doc.id}.md`} dl={dl} testid="tx-preauth" />
      )}

      {p.missing_module_flags?.length > 0 && (
        <div className="mt-8 bg-amber-500/5 border border-amber-500/30 rounded p-4">
          <div className="text-amber-300 text-sm font-semibold mb-1">Recommended additional assessments</div>
          <div className="flex flex-wrap gap-1.5">
            {p.missing_module_flags.map((m, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300">{m.replace(/_/g, " ")}</span>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

function TextBlock({ title, text, filename, dl, testid }) {
  if (!text) return <div className="mt-6 text-sm text-[var(--pa-text-dim)]">Not generated.</div>;
  return (
    <div className="mt-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="kicker">{title}</div>
        <button onClick={() => dl(filename, text)} className="flex items-center gap-1 text-xs text-[var(--pa-gold)] hover:underline">
          <Download size={12} /> Download .md
        </button>
      </div>
      <pre className="text-xs whitespace-pre-wrap bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-4 text-[var(--pa-text)] leading-relaxed font-sans">{text}</pre>
    </div>
  );
}
